import { fromEvent as observableFromEvent, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Yii } from "libraries/interfaces/Yii";
import {
    AfterViewInit, ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { IMultiselectConfig, IMultiselectOptions } from './multiselect.model';
import { IFieldSpecs } from '../interfaces/field-specs';
import { SelectItem } from "primeng/api";
import { faCheck, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { TextFieldComponent } from "libraries/module/formbuilder/fields/text-field/text-field.component";
import { TextComponent } from "libraries/module/formbuilder/fields/text/text.component";

enum eAction {
    close = "close",
    click = "click"
}

/**
 * Agnostic multiselect field
 */
@Component({
    selector: ' multiselect',
    templateUrl: './multiselect.component.html',
    styleUrls: [
        './multiselect.component.scss'
    ],
})
export class MultiselectComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @ViewChild('multiSelect')
    public multiSelect: MultiSelect;

    @ViewChildren('optionItem')
    public optionItem: QueryList<ElementRef>;

    @Input()
    public useApi: boolean = false;
    @Input()
    public fieldConfig: IFieldSpecs;
    @Input()
    public options: IMultiselectOptions[] = [];
    @Input()
    public apiOptions: IMultiselectOptions[];
    @Input()
    public disabled: boolean = false;
    @Input()
    public labelCustom: string;
    @Input()
    public isFull: boolean = false;
    @Input()
    public isPositionFixed: boolean = false;
    @Input()
    public config: IMultiselectConfig = {
        defaultLabel: Yii.t('wui-shared-js', 'Sélectionner'),
        displaySelectedLabel: true,
        // panelStyle: {minWidth: '12rem'} // breaking display in decathlon app
    };
    @Input()
    public nonAccent: boolean;
    @Input()
    public searchOnTwoCharCode: boolean;
    @Input()
    public maxSelection: number;
    @Input()
    public mustEmitChangeOnClick: boolean = true;
    @Input()
    public mustEmitChangeOnHide: boolean = false;

    @Input()
    public showHeader: boolean = true;
    @Input()
    public appendTo: string;
    @Input()
    public patchWith: string;

    @Output()
    public readonly selectionChange: EventEmitter<string[]> = new EventEmitter();
    @Output()
    public readonly onType: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('searchField')
    public readonly searchField: ElementRef;

    public readonly iconChecked: IconDefinition = faCheck;

    // TODO : selectedOptions is used to bind selected options in the multiselect via ngModel
    // but the formControl can do the same and is also used
    // in consequence their is a doublon of model binding system
    public selectedOptions: string[] = [];
    public oldSelectedOptions: string[] = [];
    public selected: string[] = [];
    public selectedId: number = 0;
    public selectedItemsLabel: string = `{0} ${Yii.t('wui-shared-js', 'options sélectionnées')}`;
    public action: typeof eAction = eAction;
    public filter: boolean = true;
    public apiGroup: FormGroup;

    private cacheOptions: IMultiselectOptions[] = [];
    private filterInputChildSubscription: Subscription;


    constructor(
        private readonly fb: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    public get fielConfigClassName(): string {
        return this.fieldConfig.className;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.fieldConfig && changes.fieldConfig.currentValue) {
            if (this.fieldConfig.disabled) {
                this.fieldConfig.formGroup.disable({emitEvent: false});
            } else {
                this.fieldConfig.formGroup.enable({emitEvent: false});
            }
            this.setSelected();
        }
        if (this.useApi && changes.apiOptions && changes.apiOptions.currentValue && this.multiSelect) {
            setTimeout(
                (): void => {
                    this.multiSelect.visibleOptions = this.apiOptions;
                    this.options = this.apiOptions;
                    this.selectedId = 0;
                    const itemValue: string = this.apiOptions[this.selectedId].value as string;
                    this.handleChange({itemValue, value: [itemValue]}, eAction.click);
                    this.patch();
                    this.changeDetectorRef.detectChanges();
                }
            );
        }
        if (changes?.options?.currentValue && this.multiSelect){
            this.multiSelect.visibleOptions = this.options;
        }
    }

    public ngOnInit(): void {
        this.setSelected();
        if (this.labelCustom) {
            this.selectedItemsLabel = `{0} ${this.labelCustom}`;
        }
        if (this.useApi) {
            this.filter = false;
            this.apiGroup = new FormGroup({apiQuery: new FormControl('')});
            this.apiGroup.get('apiQuery')
                .valueChanges
                .subscribe((value: string): void => {
                    this.onType.emit(value);
                });
        }
    }

    public handleFilter(): void {
        if (this.useApi) {
            this.options = this.apiOptions;
        }
    }

    // Listen value of filter input if empty reinitialize with all options
    public ngAfterViewInit(): void {
        this.cacheOptions = (this.options || []).slice(0);
        //this.multiSelect.filterInputChild = this.searchField;
        const formValue: string[] = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
        if (formValue && formValue.length) {
            this.selectedOptions = [...formValue];
            this.selected = [...formValue];
        }
    }

    public ngOnDestroy(): void {
        if (this.filterInputChildSubscription) {
            this.filterInputChildSubscription.unsubscribe();
        }
    }

    // Check if checkbox has to be checked or not
    public isChecked(value: string): boolean {
        if (this.mustEmitChangeOnHide) {
            return this.selected && this.selected.length && this.selected.indexOf(value) > -1;
        }
        return this.selectedOptions && this.selectedOptions.length > 0 && this.selectedOptions.indexOf(value) > -1;
    }

    // Remove class 'focus-option' when cursor overs items
    public removeFocusClass(index: number): void {
        this.selectedId = index;

        const optionFound: ElementRef = this.optionItem.find((option: ElementRef) =>
            option.nativeElement.classList.value.indexOf('focus-option') > 0
        );

        if (optionFound) {
            optionFound.nativeElement.classList.remove('focus-option');
        }
    }

    public navigateSearchField(event: KeyboardEvent): void {
        let itemValue: string;

        switch (event.key) {
            case "ArrowDown":
                event.stopPropagation();
                if (this.selectedId < this.options.length - 1) {
                    this.selectedId++;
                    itemValue = this.options[this.selectedId].value as string;
                    this.handleChange({itemValue, value: [itemValue]}, eAction.click);
                }
                break;
            case "ArrowUp":
                event.stopPropagation();
                if (this.selectedId > 0) {
                    this.selectedId--;
                    itemValue = this.options[this.selectedId].value as string;
                    this.handleChange({itemValue, value: [itemValue]}, eAction.click);
                }
                break;
            case "Tab":
                event.stopPropagation();
                if (event.shiftKey) {
                    console.log('shift + key')
                }
                this.multiSelect.hide();
                break;
            default:
                break;
        }
    }

    public navigate(event: KeyboardEvent): void {
        switch (event.keyCode) {
            case 38: { // KeyDown
                if (this.selectedId > 0) {
                    this.selectedId = this.selectedId - 1;
                }
                break;
            }

            case 40: { // KeyUp
                const optionsLength: number = this.getOptions().length;
                if (this.selectedId < optionsLength - 1) {
                    this.selectedId = this.selectedId + 1;
                }
                break;
            }

            case 13: { // Enter
                event.preventDefault();
                this.selectOption();
                break;
            }

            case 27: { // Esc
                this.multiSelect.hide();
                break;
            }

            case 9: { // Tab
                this.multiSelect.hide();
                break;
            }
            default:
                break;
        }
    }

    // on Enter, select visually the item
    public selectOption(): void {
        if (this.multiSelect.overlayVisible) {
            const options: SelectItem[] = this.getOptions();
            const selectedValue: any = options[this.selectedId].value;

            this.selectedOptions = this.selectedOptions.indexOf(selectedValue) === -1 ?
                this.add(this.selectedOptions, selectedValue) :
                this.remove(this.selectedOptions, selectedValue);
        }
    }

    public showPanel(): void {
        this.selectedId = 0;

        if (this.multiSelect.overlayVisible) {
            this.focusFilterInput();
        }

    }

    public getOptions(): SelectItem[] {
        return this.multiSelect && this.multiSelect.visibleOptions && this.multiSelect.visibleOptions.length ?
            this.multiSelect.visibleOptions :
            (this.apiOptions || this.options);
    }

    public focusFilterInput(): void {
        if (this.multiSelect && this.multiSelect.filterInputChild) {
            this.multiSelect.filterInputChild.nativeElement.focus();
        }
        if (this.multiSelect && this.multiSelect.filterInputChild) {

            this.filterInputChildSubscription =
                observableFromEvent(this.multiSelect.filterInputChild.nativeElement, 'input')
                    .pipe(
                        map((inputEvent: any) => {
                            return inputEvent.target.value;
                        }))
                    .subscribe((value: string) => {
                        this.onType.emit(value);
                        if (!this.cacheOptions || this.cacheOptions.length === 0) {
                            this.cacheOptions = (this.options || []).slice(0);
                        }

                        // Selected the first item on each filtering
                        this.selectedId = 0;
                        if (this.searchOnTwoCharCode && value && value.length === 2) {
                            this.multiSelect.visibleOptions = this.cacheOptions
                                .filter(
                                    (option: IMultiselectOptions) => {
                                        const lowerValue: string = value.toLowerCase();
                                        const lowerCode: string = (option.value as string).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                                        return lowerCode.includes(lowerValue);
                                    })
                                .sort();
                        } else if (this.nonAccent) {
                            this.multiSelect.visibleOptions = this.cacheOptions
                                .filter(
                                    (option: IMultiselectOptions) => {
                                        const nonAccentValue: string = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                                        const nonAccentLabel: string = option.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                                        return nonAccentLabel.includes(nonAccentValue);
                                    })
                                .sort();
                        }

                        if (value === '') {
                            this.multiSelect.visibleOptions = this.cacheOptions;
                        }
                    });

        }
    }

    public handleChange(selection: { value: string[], itemValue: string }, action: eAction): void {
        if (this.maxSelection && action === eAction.click && selection) {
            const newSelection: string[] = selection.itemValue === this.oldSelectedOptions[0] ?
                [] :
                [selection.itemValue];
            this.oldSelectedOptions = newSelection;
            if (this.mustEmitChangeOnClick) {
                this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).patchValue(newSelection);
                this.selectionChange.emit(newSelection);
            }
            this.selectedOptions = newSelection;
        }
        if (!this.maxSelection && !this.mustEmitChangeOnHide && action === eAction.click && selection) {
            const newSelection: string[] = selection.value;
            this.oldSelectedOptions = newSelection;
            this.selectedOptions = newSelection;
            if (this.mustEmitChangeOnClick) {
                this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).patchValue(newSelection);
                this.selectionChange.emit(this.selectedOptions);
            }
        }
        if (this.mustEmitChangeOnHide && action === eAction.click && selection) {
            this.selected = [...selection.value];
        }

        if (this.mustEmitChangeOnHide && action === eAction.close) {
            this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).patchValue(this.selected);
        }
    }

    private add(options: string[], element: string): string[] {
        return options.concat([element]);
    }

    private remove(array: string[], element: string): string[] {
        return array.filter((item: string): boolean => item !== element);
    }

    private setSelected(): void {
        const formValue: string[] = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
        if (formValue && formValue.length) {
            this.selectedOptions = [...formValue];
            this.selected = [...formValue];
        }
    }

    private patch(): void {
        if (this.patchWith && this.options) {

            const relatedIndex: number = this.options.findIndex(
                (option: IMultiselectOptions): boolean => option.value === this.patchWith
            );
            if (relatedIndex > -1) {
                this.selectedId = relatedIndex;
                const newSelection: string[] = [this.patchWith];
                this.oldSelectedOptions = newSelection;
                if (this.mustEmitChangeOnClick) {
                    this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).patchValue(newSelection);
                    this.selectionChange.emit(newSelection);
                }
                this.selectedOptions = newSelection;
            }
            this.patchWith = null;
        }
    }
}
